import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';

/**
 * @guard AuthGuard
 * @description Route guard that protects routes requiring authentication. Acts as a gatekeeper
 * for authenticated routes by verifying the presence of a valid authentication token in the browser's
 * localStorage. Redirects unauthenticated users to the login page.
 * 
 * Implementation of Angular's CanActivate interface to control route access.
 * Applied to routes that should only be accessible to authenticated users.
 * 
 * Authentication Flow:
 * 1. User attempts to access a protected route
 * 2. AuthGuard.canActivate() is triggered
 * 3. Guard checks for 'token' key in localStorage
 * 4. If token exists: Route is allowed (returns true)
 * 5. If token missing: User is redirected to /login (returns false)
 * 
 * Responsibilities:
 * - Verify authentication token presence in localStorage
 * - Redirect unauthenticated users to login page
 * - Prevent unauthorized access to protected routes
 * - Enable seamless user authentication flow
 * 
 * Key Features:
 * - Simple token-based authentication check
 * - Automatic redirection to login on authentication failure
 * - Lightweight and efficient route protection
 * - Global guard available to all routes (provided in root)
 * 
 * @implements {CanActivate}
 * @providedIn 'root' - Guard is available application-wide
 * 
 * @example
 * // Usage in routing module
 * const routes: Routes = [
 *   { 
 *     path: 'dashboard', 
 *     component: DashboardComponent,
 *     canActivate: [AuthGuard]
 *   },
 *   {
 *     path: 'subscriptions',
 *     component: SubscriptionComponent,
 *     canActivate: [AuthGuard]
 *   }
 * ];
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  /**
   * @property {Router} router
   * @description Angular Router instance for programmatic navigation.
   * Used to redirect unauthenticated users to the login page when
   * authentication token is not found in localStorage.
   * Marked as readonly to prevent accidental reassignment.
   */
  public readonly router: Router;

  /**
   * @method constructor
   * @description Initializes the AuthGuard with Angular's Router service for navigation operations.
   * The router instance is stored as a readonly property to ensure it cannot be
   * accidentally replaced during the guard's lifecycle.
   * 
   * @param {Router} router - Angular Router service provided by Angular's dependency injection system.
   * Used for programmatic navigation when authentication fails.
   */
  constructor(router: Router) {
    this.router = router;
  }

  /**
   * @method canActivate
   * @description Determines whether a route can be activated based on authentication status.
   * This method is called by Angular's router before navigating to a protected route.
   * 
   * Authentication Check:
   * - Attempts to retrieve 'token' from localStorage
   * - If token exists: Route access is granted (returns true)
   * - If token is missing/null: User is redirected to '/login' (returns false)
   * 
   * The guard does not validate token format or expiration—it only checks for presence.
   * For production applications, consider adding token validation and refresh logic.
   * 
   * @param {ActivatedRouteSnapshot} [_route] - Snapshot of the activated route component.
   * Currently unused but available for route-specific guard logic in future enhancements.
   * @param {RouterStateSnapshot} [_state] - Snapshot of the router state containing the full URL.
   * Currently unused but available for state-specific guard logic in future enhancements.
   * 
   * @returns {boolean} True if user has authentication token (route is allowed),
   * false if token is missing (route is blocked and user is redirected to login)
   * 
   * @example
   * // Route definition with AuthGuard
   * const routes: Routes = [
   *   {
   *     path: 'dashboard',
   *     component: DashboardComponent,
   *     canActivate: [AuthGuard]  // canActivate() will be called automatically
   *   }
   * ];
   * 
   * // When user navigates to /dashboard:
   * // 1. If token exists in localStorage -> Dashboard loads (returns true)
   * // 2. If token missing -> Redirects to /login (returns false)
   */
  canActivate(
    _route?: ActivatedRouteSnapshot,
    _state?: RouterStateSnapshot
  ): boolean {
    if (!localStorage.getItem('token')) {
      this.router.navigate(['/login']);
      return false;
    }
    return true;
  }
}
